import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import PropTypes, { shape } from 'prop-types';
import { connect } from 'react-redux';
import { Form, reduxForm } from 'redux-form';
import Moment from 'moment-timezone';
import { Button } from 'reactstrap';
import SelectionableTable from '../../../../common/forms/table/SelectionableTable';
import FormFooter from '../../../../common/forms/FormFooter';
import { ITINERARY_FOR_COUNTER_PASSENGER_FLOW_COLUMNS } from '../../../../../config/columns';
import Loader from '../../../../common/Loader';
import {
  deleteBoardingDraft,
  getPassengerFlowForItinerary,
  postAdvanceBoardingForItinerary,
  postBoardingDraftByItineraryId,
} from '../../../../../actions';
import Modal from '../../../../common/modal/Modal';
import ResourceProperty from '../../../../common/resource/ResourceProperty';
import { DATE_TIME_FORMAT, TIME_ZONE } from '../../../../../config/locale';
import ItineraryBoardingMotiveForm from './ItineraryBoardingMotiveForm';
import { TICKET_BOARDING_TIME_STATUS } from '../../../../../config/constants';
import Badge from '../../../../common/Badge';

const buildLocationsList = (itineraryScheduleSegments) => {
  if (!itineraryScheduleSegments || itineraryScheduleSegments.length === 0) {
    return [];
  }

  const sortedSegments = [...itineraryScheduleSegments].sort(
    (a, b) => a.startOffset - b.startOffset,
  );

  const locations = [];

  const firstSegment = sortedSegments[0];
  locations.push({
    locationIndex: 0,
    locationId: firstSegment.segment.sourceLocation.id,
    locationName: firstSegment.segment.sourceLocation.name,
    durationToThisLocation: 0,
  });

  sortedSegments.forEach((segment, index) => {
    const destLocation = segment.segment.destinationLocation;
    const durationToThisLocation =
      segment.startOffset +
      (segment.segment.duration || 0) +
      (segment.segmentAdjustment || 0);

    locations.push({
      locationIndex: index + 1,
      locationId: destLocation.id,
      locationName: destLocation.name,
      durationToThisLocation,
    });
  });

  return locations;
};

const calculateDepartureTimeFromLocation = (
  departureTimestamp,
  durationMinutes,
) => {
  const baseTime = Moment(departureTimestamp).tz(TIME_ZONE);
  const departureTime = baseTime.add(durationMinutes, 'minutes');
  return departureTime;
};

const requiresLateReason = (departureTimeFromLocation) => {
  const now = Moment().tz(TIME_ZONE);
  return now.isAfter(departureTimeFromLocation);
};

export const ItineraryForCounterPassengerFlowForm = ({
  flow,
  handleSubmit,
  onSubmit,
  loading,
  type,
  index,
  dispatchPostBoardingDraftByItineraryId,
  itineraryId,
  dispatchDeleteBoardingDraft,
  itinerary,
  dispatchGetPassengerFlowForItinerary,
  loadingBoardingDraft,
  currentBoardingLocationId,
  currentBoardingLocationName,
  dispatchPostAdvanceBoardingForItinerary,
}) => {
  const tableRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showDifferentLocationModal, setShowDifferentLocationModal] =
    useState(false);

  const [pendingBoardingPassenger, setPendingBoardingPassenger] =
    useState(null);

  const [showDisembarkModal, setShowDisembarkModal] = useState(false);
  const [pendingDisembarkPassenger, setPendingDisembarkPassenger] =
    useState(null);

  useLayoutEffect(() => {
    dispatchGetPassengerFlowForItinerary({ itineraryId });
  }, []);

  const flowData = flow[`${type}Flow`];

  const isBoarding = type === 'boarding';

  const hasDefinitiveClosure = flow.boardingClosed;

  const canEdit = Boolean(flow.canEdit);

  const canAdvanceBoarding = flow ? flow.canAdvanceBoarding : false;

  const locations = useMemo(() => {
    if (!itinerary.itineraryScheduleSegmentList) return [];
    const locationList = buildLocationsList(
      itinerary.itineraryScheduleSegmentList,
    );
    return locationList.map((location) => ({
      ...location,
      label: location.locationName,
      value: location.locationId,
    }));
  }, [itinerary.itineraryScheduleSegmentList]);

  const currentLocation = useMemo(() => {
    if (!locations.length || index >= locations.length) return null;
    return locations[index];
  }, [locations, index]);

  const departureTimeFromLocation = useMemo(() => {
    if (!currentLocation || !itinerary.estimatedDepartureTime) return null;
    return calculateDepartureTimeFromLocation(
      itinerary.estimatedDepartureTime,
      currentLocation.durationToThisLocation,
    );
  }, [currentLocation, itinerary.estimatedDepartureTime]);

  const handleAdvanceBoarding = async () => {
    if (!isBoarding) return;
    await dispatchPostAdvanceBoardingForItinerary({
      itineraryId,
      locationId: currentLocation.locationId,
    });
  };

  const processBoardingDraft = async (passengerData, motiveData) => {
    if (!isBoarding) return;

    const { ticketId, customerId, kidsOnItineraryId } = passengerData;

    const boardingDraft = {
      itineraryId: Number(itineraryId),
      ticketId,
      customerId,
      locationIndex: index,
      kidsOnItineraryId,
      locationId: currentLocation ? currentLocation.locationId : null,
    };

    if (motiveData && motiveData.reason) {
      const { reason } = motiveData;
      boardingDraft.movementReasonId = reason.value;
    }

    const isDifferentLocation =
      currentBoardingLocationId && currentLocation
        ? currentBoardingLocationId !== currentLocation.locationId
        : false;

    if (hasDefinitiveClosure || isDifferentLocation) {
      boardingDraft.otherBoardingLocationId = currentBoardingLocationId;
      boardingDraft.hasDefinitiveClosure = hasDefinitiveClosure;
    }

    if (isDifferentLocation) {
      boardingDraft.locationId = currentBoardingLocationId;
    }

    await dispatchPostBoardingDraftByItineraryId({
      itineraryId,
      boardingDraft,
    });

    if (hasDefinitiveClosure) {
      dispatchGetPassengerFlowForItinerary({ itineraryId });
    }
  };

  const revertCheckboxSelection = (toggledId, action) => {
    setTimeout(() => {
      if (tableRef.current && tableRef.current.getWrappedInstance) {
        const instance = tableRef.current.getWrappedInstance();
        const method = action === 'select' ? 'selectItem' : 'unselectItem';

        if (instance && instance[method]) {
          instance[method](toggledId);
        }
      }
      setIsProcessing(true);
    }, 0);
  };

  const handleBoardingSelection = (passengerData) => {
    const isDifferentLocation =
      currentBoardingLocationId && currentLocation
        ? currentBoardingLocationId !== currentLocation.locationId
        : false;

    if (!hasDefinitiveClosure && !isDifferentLocation) {
      processBoardingDraft(passengerData, null);
      return;
    }

    setPendingBoardingPassenger(passengerData);
    setShowDifferentLocationModal(true);
    revertCheckboxSelection(passengerData.toggledId, 'unselect');
  };

  const processDisembarkDraft = async (passengerData, motiveData) => {
    const { ticketId, customerId, kidsOnItineraryId } = passengerData;

    const boardingDraft = {
      itineraryId: Number(itineraryId),
      ticketId,
      customerId,
      locationIndex: index,
      kidsOnItineraryId,
      locationId: currentLocation ? currentLocation.locationId : null,
    };

    if (motiveData && motiveData.reason) {
      const { reason } = motiveData;
      boardingDraft.movementReasonId = reason.value;
    }

    await dispatchDeleteBoardingDraft({ itineraryId, boardingDraft });

    dispatchGetPassengerFlowForItinerary({ itineraryId });
  };

  const handleDisembarkSelection = (passengerData) => {
    setPendingDisembarkPassenger(passengerData);
    setShowDisembarkModal(true);
    revertCheckboxSelection(passengerData.toggledId, 'select');
  };

  const onChangeTable = (item) => {
    const { isToggleAll, programmatic, row, isSelected, toggledId } = item;

    if (isToggleAll || programmatic) return;

    if (!isBoarding) return;

    const {
      ticketTripRequestId: ticketId,
      customerId,
      kidsOnItineraryId,
    } = row;
    const passengerData = {
      row,
      toggledId,
      ticketId,
      customerId,
      kidsOnItineraryId,
    };

    if (isSelected) {
      handleBoardingSelection(passengerData);
    } else {
      handleDisembarkSelection(passengerData);
    }
  };

  const handleDifferentLocationFormSubmit = async (formValues) => {
    if (!pendingBoardingPassenger) return;

    if (tableRef.current && tableRef.current.getWrappedInstance) {
      const instance = tableRef.current.getWrappedInstance();
      if (instance && instance.selectItem) {
        instance.selectItem(pendingBoardingPassenger.toggledId);
      }
    }

    await processBoardingDraft(pendingBoardingPassenger, formValues);

    setShowDifferentLocationModal(false);
    setPendingBoardingPassenger(null);
    setIsProcessing(false);

    if (hasDefinitiveClosure) {
      await dispatchGetPassengerFlowForItinerary({ itineraryId });
    }
  };

  const handleDifferentLocationCancel = () => {
    setShowDifferentLocationModal(false);
    setPendingBoardingPassenger(null);
    setIsProcessing(false);
  };

  const handleDisembarkFormSubmit = async (formValues) => {
    if (!pendingDisembarkPassenger) return;

    if (tableRef.current && tableRef.current.getWrappedInstance) {
      const instance = tableRef.current.getWrappedInstance();
      if (instance && instance.unselectItem) {
        instance.unselectItem(pendingDisembarkPassenger.toggledId);
      }
    }

    await processDisembarkDraft(pendingDisembarkPassenger, formValues);

    setShowDisembarkModal(false);
    setPendingDisembarkPassenger(null);
    setIsProcessing(false);

    if (hasDefinitiveClosure) {
      await dispatchGetPassengerFlowForItinerary({ itineraryId });
    }
  };

  const handleDisembarkCancel = () => {
    setShowDisembarkModal(false);
    setPendingDisembarkPassenger(null);
    setIsProcessing(false);
  };

  const onHandleSubmit = (formValues) => {
    const updatedItems = formValues.items.map((item) => {
      const ticketData = flowData.find(
        (ticket) =>
          ticket.correlative === item.id &&
          ticket.kidsOnItineraryId === item.original.kidsOnItineraryId,
      );
      return {
        ...item,
        ticket: ticketData.ticket,
        kidsOnItineraryId: ticketData.kidsOnItineraryId,
      };
    });

    onSubmit({
      items: updatedItems,
      type,
      index,
    });

    dispatchGetPassengerFlowForItinerary({ itineraryId });
  };

  const differentLocationModalContent = () => (
    <div>
      <div className="p-3">
        <p>
          El pasajero está embarcando en una ubicación diferente a la
          programada.
        </p>
        {pendingBoardingPassenger && (
          <div>
            <ResourceProperty label="Pasajero:">
              {pendingBoardingPassenger.row.customerFullName}
            </ResourceProperty>
            <ResourceProperty label="Boleto:">
              {pendingBoardingPassenger.row.ticketCode}
            </ResourceProperty>
            <ResourceProperty label="Asiento:">
              {pendingBoardingPassenger.row.seatNumber}
            </ResourceProperty>
            {currentLocation && (
              <div>
                <ResourceProperty label="Embarque elegido durante la compra:">
                  {currentLocation.locationName}
                </ResourceProperty>
                <ResourceProperty label="Embarque actual:">
                  {currentBoardingLocationName || '-'}
                </ResourceProperty>
              </div>
            )}
          </div>
        )}
      </div>
      <ItineraryBoardingMotiveForm
        onSubmit={handleDifferentLocationFormSubmit}
        onCancel={handleDifferentLocationCancel}
        isBoarding
        loadingBoardingDraft={loadingBoardingDraft}
      />
    </div>
  );

  const disembarkModalContent = () => (
    <div>
      <div className="p-3">
        <p>Está seguro que desea desmarcar este pasajero?</p>
        {pendingDisembarkPassenger && (
          <div>
            <ResourceProperty label="Pasajero:">
              {pendingDisembarkPassenger.row.customerFullName}
            </ResourceProperty>
            <ResourceProperty label="Boleto:">
              {pendingDisembarkPassenger.row.ticketCode}
            </ResourceProperty>
            <ResourceProperty label="Asiento:">
              {pendingDisembarkPassenger.row.seatNumber}
            </ResourceProperty>
          </div>
        )}
      </div>
      <ItineraryBoardingMotiveForm
        onSubmit={handleDisembarkFormSubmit}
        onCancel={handleDisembarkCancel}
        isLeaving
        hasDefinitiveClosure={hasDefinitiveClosure}
        locationList={locations}
        dismarkModal
        loadingBoardingDraft={loadingBoardingDraft}
      />
    </div>
  );

  if (loading) return <Loader />;

  const isItemSelectable = (row) => {
    if (!isBoarding && flow.canCheckOut && !row.hasCompletedTrip) {
      return true;
    }

    if (!isBoarding && row.hasCompletedTrip) return false;

    if (!canEdit) return false;

    if (isBoarding) {
      return true;
    }

    return true;
  };

  return (
    <div>
      <Modal
        show={showDifferentLocationModal}
        title="Mótivo de ubicación diferente"
        body={differentLocationModalContent()}
      />

      <Modal
        show={showDisembarkModal}
        title="Confirmar desembarque"
        body={disembarkModalContent()}
      />

      {isBoarding && canAdvanceBoarding && (
        <Button
          color="secondary pull-right"
          disabled={!canAdvanceBoarding}
          onClick={handleAdvanceBoarding}
        >
          <i className="fa fa-bus mr-1" /> <span>Adelantar Embarque</span>
        </Button>
      )}

      {currentLocation && departureTimeFromLocation && (
        <>
          <ResourceProperty label="Hora de salida programada:">
            {departureTimeFromLocation.format(DATE_TIME_FORMAT)}
          </ResourceProperty>
          <ResourceProperty label="Estado:">
            {requiresLateReason(departureTimeFromLocation) ? (
              <Badge
                color={TICKET_BOARDING_TIME_STATUS.LATE.color}
                text={TICKET_BOARDING_TIME_STATUS.LATE.label}
              />
            ) : (
              <Badge
                color={TICKET_BOARDING_TIME_STATUS.EARLY.color}
                text={TICKET_BOARDING_TIME_STATUS.EARLY.label}
              />
            )}
          </ResourceProperty>
          <ResourceProperty label="Ubicación actual (Embarque): ">
            <strong>{currentBoardingLocationName || '-'}</strong>
          </ResourceProperty>
        </>
      )}

      <h5>
        Seleccione los pasajeros que{' '}
        {isBoarding ? 'embarcaron' : 'desembarcaron'}
      </h5>
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        <SelectionableTable
          ref={tableRef}
          columns={ITINERARY_FOR_COUNTER_PASSENGER_FLOW_COLUMNS}
          data={flowData}
          defaultPageSize={flowData.length}
          keyField="correlative"
          form="ItineraryForCounterPassengerFlowForm"
          showPagination={false}
          onSelectionChange={onChangeTable}
          showSelectAll={false}
          activeField="hasBoardingDraft"
          disabled={isProcessing}
          isItemSelectable={isItemSelectable}
          returnFullObject
        />

        {isBoarding &&
          !hasDefinitiveClosure &&
          currentBoardingLocationId === currentLocation.locationId &&
          canEdit && <FormFooter />}
        {!isBoarding && <FormFooter />}
      </Form>
    </div>
  );
};

ItineraryForCounterPassengerFlowForm.propTypes = {
  flow: PropTypes.instanceOf(Object).isRequired,
  loading: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  dispatchPostBoardingDraftByItineraryId: PropTypes.func.isRequired,
  itineraryId: PropTypes.number.isRequired,
  dispatchDeleteBoardingDraft: PropTypes.func.isRequired,
  itinerary: shape({
    itineraryScheduleSegmentList: PropTypes.arrayOf(
      shape({
        segment: shape({
          sourceLocation: shape({
            id: PropTypes.number,
            name: PropTypes.string,
          }),
          destinationLocation: shape({
            id: PropTypes.number,
            name: PropTypes.string,
          }),
          duration: PropTypes.number,
        }),
        startOffset: PropTypes.number,
        segmentAdjustment: PropTypes.number,
      }),
    ),
    estimatedDepartureTime: PropTypes.number,
  }).isRequired,
  dispatchGetPassengerFlowForItinerary: PropTypes.func.isRequired,
  loadingBoardingDraft: PropTypes.bool,
  currentBoardingLocationId: PropTypes.number,
  currentBoardingLocationName: PropTypes.string,
  dispatchPostAdvanceBoardingForItinerary: PropTypes.func.isRequired,
};

ItineraryForCounterPassengerFlowForm.defaultProps = {
  loading: false,
  loadingBoardingDraft: false,
  currentBoardingLocationId: null,
  currentBoardingLocationName: null,
};

const mapStateToProps = ({ ItineraryUnit }, { index }) => ({
  loading: ItineraryUnit.Itinerary.getIn(['current', 'flowLoading']),
  flow: ItineraryUnit.Itinerary.getIn(['current', 'passengerFlow']).get(
    'passengerFlowForLocationList',
  )[index],
  itinerary: ItineraryUnit.Itinerary.getIn(['current', 'content']).toJS(),
  loadingBoardingDraft: !ItineraryUnit.BoardingDraft.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  currentBoardingLocationId: ItineraryUnit.Itinerary.getIn([
    'current',
    'passengerFlow',
    'currentBoardingLocationId',
  ]),
  currentBoardingLocationName: ItineraryUnit.Itinerary.getIn([
    'current',
    'passengerFlow',
    'currentBoardingLocationName',
  ]),
});

const dispatchToProps = {
  dispatchPostBoardingDraftByItineraryId: postBoardingDraftByItineraryId,
  dispatchDeleteBoardingDraft: deleteBoardingDraft,
  dispatchGetPassengerFlowForItinerary: getPassengerFlowForItinerary,
  dispatchPostAdvanceBoardingForItinerary: postAdvanceBoardingForItinerary,
};

const formComponent = reduxForm({
  form: 'ItineraryForCounterPassengerFlowForm',
})(ItineraryForCounterPassengerFlowForm);

export default connect(mapStateToProps, dispatchToProps)(formComponent);
